import React, { useState, useEffect, useRef, useCallback } from "react";
import { database } from "../firebaseConfig";
import { ref, get, onValue, update } from "firebase/database";
import "./LeadSpySettings.css";

const WA_MODULES = {
  webSystem: {
    id: 'webSystem',
    name: 'Web-System',
    description: 'Detect the CMS / shop system, version, edition and tech stack maturity',
    icon: 'fa-server',
    credits: 1,
    isRequired: true,
    features: ['CMS Detection', 'Version Estimate', 'Edition Estimate', 'Shop Detection', 'Tech Stack Maturity'],
    targetFields: ['CMS', 'Version', 'Shop Type'],
  },
  productCount: {
    id: 'productCount',
    name: 'Shop Product Count',
    description: 'Estimate the number of products and categories in the online shop',
    icon: 'fa-box-open',
    credits: 1,
    isRequired: false,
    features: ['Product Count (est.)', 'Category Count'],
    targetFields: ['Product Count', 'Category Count'],
  },
  websiteAge: {
    id: 'websiteAge',
    name: 'Website Age',
    description: 'Analyze website modernity — technology signals, era estimate, and age score',
    icon: 'fa-hourglass-half',
    credits: 2,
    isRequired: false,
    isPremium: true,
    features: ['Age Score (0-100)', 'Modernity Label', 'Era Estimate', 'Old / Modern Signals', 'Detailed Findings'],
    targetFields: ['Website Age Score', 'Age Label'],
  },
};

const DEBOUNCE_MS = 1200;

const WebsiteAnalyzerSettings = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [activeModules, setActiveModules] = useState(['webSystem']);
  const [userCredits, setUserCredits] = useState(0);
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, creditsUsed: 0 });

  const debounceRef = useRef(null);
  const savedTimerRef = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    const settingsRef = ref(database, `/World/${userId}/settings/websiteAnalyzerSettings`);
    get(settingsRef).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        if (data.activeModules) setActiveModules(data.activeModules);
      }
      setLoading(false);
      requestAnimationFrame(() => { hasLoadedRef.current = true; });
    });

    const creditsRef = ref(database, `/World/${userId}/credits`);
    const unsubCredits = onValue(creditsRef, (snapshot) => {
      if (snapshot.exists()) {
        const wallets = snapshot.val() || {};
        setUserCredits((Number(wallets.spyCredits) || 0) + (Number(wallets.bonusCredits) || 0));
      }
    });

    const statsRef = ref(database, `/World/${userId}/stats/websiteAnalyzer`);
    const unsubStats = onValue(statsRef, (snapshot) => {
      if (snapshot.exists()) setStats(snapshot.val());
    });

    return () => {
      unsubCredits();
      unsubStats();
    };
  }, [userId]);

  const persistSettings = useCallback(async (modules) => {
    if (!userId) return;
    setSaveStatus('saving');
    try {
      const settingsRef = ref(database, `/World/${userId}/settings/websiteAnalyzerSettings`);
      await update(settingsRef, {
        activeModules: modules,
        updatedAt: Date.now(),
      });
      setSaveStatus('saved');
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2400);
    } catch (error) {
      console.error("Error saving website analyzer settings:", error);
      setSaveStatus('idle');
    }
  }, [userId]);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    clearTimeout(debounceRef.current);
    setSaveStatus('idle');
    debounceRef.current = setTimeout(() => {
      persistSettings(activeModules);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [activeModules, persistSettings]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(savedTimerRef.current);
    };
  }, []);

  const calculateCredits = (modules = activeModules) => {
    return modules.reduce((total, moduleId) => {
      const mod = WA_MODULES[moduleId];
      return total + (mod?.credits || 0);
    }, 0);
  };

  const toggleModule = (moduleId) => {
    const mod = WA_MODULES[moduleId];
    if (mod?.isRequired) return;
    setActiveModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  if (loading) {
    return (
      <div className="spySettingsLoading">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  const totalCredits = calculateCredits();

  return (
    <div className="spySettingsContainer">
      <div className="spySettingsHeader">
        <div className="spySettingsTitle">
          <div className="spySettingsIcon">
            <i className="fa fa-laptop-code" style={{ fontSize: 22, color: '#e67e22' }} />
          </div>
          <div className="spySettingsTitleText">
            <h3>Website Analyzer</h3>
            <p>Scan websites for CMS, shop data, and technology maturity</p>
          </div>
        </div>
        <div className="spyCreditsDisplay">
          <div className="creditsBalance">
            <span className="creditsLabel">Available Credits</span>
            <span className="creditsValue">{userCredits.toLocaleString()}</span>
          </div>
          <div className="creditsCost">
            <span className="costLabel">Per Scan</span>
            <span className="costValue">{totalCredits} Credits</span>
          </div>
        </div>
      </div>

      <div className="spyModulesSection">
        <div className="modulesSectionHeader">
          <h4>
            <i className="fa fa-puzzle-piece"></i>
            Data Modules
          </h4>
          <div className="modulesSummary">
            <span className="modulesActive">{activeModules.length} active</span>
            <span className="modulesDivider">&bull;</span>
            <span className="modulesCredits">{totalCredits} credits/scan</span>
          </div>
        </div>

        <div className="modulesGrid">
          {Object.entries(WA_MODULES).map(([moduleId, mod]) => (
            <div
              key={moduleId}
              className={`moduleCard ${activeModules.includes(moduleId) ? 'active' : ''} ${mod.isRequired ? 'required' : ''} ${mod.isPremium ? 'premium' : ''}`}
              onClick={() => toggleModule(moduleId)}
            >
              <div className="moduleCardHeader">
                <div className="moduleIcon">
                  <i className={`fa ${mod.icon}`}></i>
                </div>
                <div className="moduleToggle">
                  {mod.isRequired ? (
                    <span className="requiredBadge">Required</span>
                  ) : (
                    <div className={`toggleSwitch ${activeModules.includes(moduleId) ? 'on' : ''}`}>
                      <div className="toggleKnob"></div>
                    </div>
                  )}
                </div>
              </div>

              <div className="moduleCardBody">
                <h5 className="moduleName">
                  {mod.name}
                  {mod.isPremium && <i className="fa fa-crown premiumIcon"></i>}
                </h5>
                <p className="moduleDesc">{mod.description}</p>
              </div>

              <div className="moduleCardFooter">
                <div className="moduleFeatures">
                  {mod.features.slice(0, 3).map((feature, idx) => (
                    <span key={idx} className="moduleFeature">
                      <i className="fa fa-check"></i>
                      {feature}
                    </span>
                  ))}
                  {mod.features.length > 3 && (
                    <span className="moduleFeatureMore">+{mod.features.length - 3} more</span>
                  )}
                </div>
                <div className="moduleCredits">
                  <span className="creditValue">{mod.credits}</span>
                  <span className="creditLabel">credits</span>
                </div>
              </div>

              <div className="moduleTargetFields">
                <i className="fa fa-database"></i>
                <span className="targetFieldsLabel">Saves to:</span>
                <span className="targetFieldsList">
                  {mod.targetFields.map((field, idx) => (
                    <code key={idx} className="targetField">{field}</code>
                  ))}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="creditCalculatorSection">
        <div className="calculatorCard">
          <div className="calculatorHeader">
            <h4>
              <i className="fa fa-calculator"></i>
              Credit Calculator
            </h4>
          </div>
          <div className="calculatorBody">
            <div className="calculatorRow">
              <span className="calcLabel">Credits per Scan</span>
              <span className="calcValue">{totalCredits}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">Your Balance</span>
              <span className="calcValue">{userCredits.toLocaleString()}</span>
            </div>
            <div className="calculatorDivider"></div>
            <div className="calculatorRow highlight">
              <span className="calcLabel">Possible Scans</span>
              <span className="calcValue">{totalCredits > 0 ? Math.floor(userCredits / totalCredits).toLocaleString() : '---'}</span>
            </div>
          </div>
        </div>

        <div className="calculatorCard statsCard">
          <div className="calculatorHeader">
            <h4>
              <i className="fa fa-chart-bar"></i>
              Usage Statistics
            </h4>
          </div>
          <div className="calculatorBody">
            <div className="calculatorRow">
              <span className="calcLabel">Total Scans</span>
              <span className="calcValue">{stats.total?.toLocaleString() || 0}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">This Month</span>
              <span className="calcValue">{stats.thisMonth?.toLocaleString() || 0}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">Credits Used</span>
              <span className="calcValue">{(stats.creditsUsed || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="spyModulesSection" style={{ marginTop: 16 }}>
        <div className="modulesSectionHeader">
          <h4>
            <i className="fa fa-gift"></i>
            Bonus Data (always included)
          </h4>
        </div>
        <div className="modulesGrid">
          <div className="moduleCard active required" style={{ cursor: 'default' }}>
            <div className="moduleCardBody">
              <h5 className="moduleName">Free Insights</h5>
              <p className="moduleDesc">Included with every scan at no extra cost</p>
            </div>
            <div className="moduleCardFooter">
              <div className="moduleFeatures">
                <span className="moduleFeature"><i className="fa fa-check"></i>Lead Score</span>
                <span className="moduleFeature"><i className="fa fa-check"></i>Industry</span>
                <span className="moduleFeature"><i className="fa fa-check"></i>Company Name</span>
                <span className="moduleFeature"><i className="fa fa-check"></i>City</span>
                <span className="moduleFeature"><i className="fa fa-check"></i>Recommended Sales Angle</span>
              </div>
              <div className="moduleCredits">
                <span className="creditValue">0</span>
                <span className="creditLabel">credits</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`spyAutoSaveIndicator ${saveStatus}`}>
        {saveStatus === 'saving' && (
          <>
            <span className="autoSaveSpinner"></span>
            <span>Saving...</span>
          </>
        )}
        {saveStatus === 'saved' && (
          <>
            <i className="fa fa-check"></i>
            <span>Saved</span>
          </>
        )}
      </div>
    </div>
  );
};

export default WebsiteAnalyzerSettings;
